import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { auth, type AuthUser } from "../lib/auth";
import { api } from "../lib/api";
import { isUserPremium } from "../lib/quota";
import { usePlayer } from "../store/player";
import { ROUTES } from "../routes/paths";
import { CrbtIcon, MainIcon, MenuIcon, MtvIcon, MusicIcon, TopArtistIcon } from "./LegacyIcons";
import { SearchIcon, SparklesIcon, PlusIcon, BellIcon } from "./DetailIcons";
import { MiniPlayer } from "./MiniPlayer";
import { GlobalAudio } from "./GlobalAudio";
import { AddActionModal } from "./AddActionModal";
import { SubscriptionPromptModal } from "./SubscriptionPromptModal";
import { WebSocketNotification } from "./WebSocketNotification";
import { trackClickGoPremium } from "../lib/analytics";
import { getImage } from "../lib/utils";
import { getEligiblePremiumPromotion } from "../lib/promotion";
import type { PremiumPromotion } from "../lib/promotion";
import { APP_EVENTS } from "../lib/appEvents";
import { StartupPremiumPromotion } from "./StartupPremiumPromotion";
import styles from "./AppShell.module.css";

const tabs = [
  { to: ROUTES.HOME, Icon: MainIcon, key: "home" },
  { to: ROUTES.MUSIC, Icon: MusicIcon, key: "playlist" },
  { to: ROUTES.ARTISTS, Icon: TopArtistIcon, key: "artist" },
  { to: ROUTES.TUNES, Icon: CrbtIcon, key: "browse" },
  { to: ROUTES.VIDEOS, Icon: MtvIcon, key: "mtv" },
  { to: ROUTES.MENU, Icon: MenuIcon, key: "menu" },
];

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { current } = usePlayer();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [homeReady, setHomeReady] = useState(false);
  const [promotionHandledForPageSession, setPromotionHandledForPageSession] = useState(false);
  const [startupPromotion, setStartupPromotion] = useState<{
    promotion: PremiumPromotion;
    user: AuthUser;
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => auth.user());

  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: api.profile,
    enabled: !!auth.access(),
  });

  // Listen to auth changes, quota updates, and storage events
  useEffect(() => {
    const handleAuthChange = () => {
      setCurrentUser(auth.user());
    };
    window.addEventListener("ym_auth_changed", handleAuthChange);
    window.addEventListener("ym_quota_updated", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);
    return () => {
      window.removeEventListener("ym_auth_changed", handleAuthChange);
      window.removeEventListener("ym_quota_updated", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    setCurrentUser(auth.user());
  }, [location.pathname]);

  useEffect(() => {
    if (profileQuery.data?.user) {
      const u = profileQuery.data.user;
      const storedUser = auth.user();
      const profile = profileQuery.data?.profile as any;
      auth.saveUser({
        ...storedUser,
        ...u,
        msisdn: u.msisdn || profile?.msisdn || storedUser?.msisdn,
        phone_number: u.phone_number || profile?.phone_number || storedUser?.phone_number,
        mobile_number: u.mobile_number || profile?.mobile_number || storedUser?.mobile_number,
        profile_picture: profile?.profile_picture || u.profile_picture || storedUser?.profile_picture,
      });
      setCurrentUser(auth.user());

      const phone = u.msisdn || profile?.msisdn || storedUser?.msisdn;
      if (phone) {
        api.subscriptions(phone).catch(() => {});
      }
    }
  }, [profileQuery.data]);

  // api.profile() normalizes and stores the combined user/profile payload.
  // Prefer that canonical user so ATOM identity fields are not lost when the
  // raw profile response keeps the phone number outside `user`.
  const user = currentUser || profileQuery.data?.user;
  const avatarUrl = getImage(
    (profileQuery.data?.profile as any)?.profile_picture ||
    profileQuery.data?.user?.profile_picture ||
    currentUser?.image ||
    currentUser?.profile_picture ||
    (currentUser as any)?.profile?.profile_picture
  );
  const { t } = useTranslation();

  useEffect(() => {
    const handleHomeReady = () => {
      setHomeReady(true);
    };
    window.addEventListener(APP_EVENTS.HOME_READY, handleHomeReady);
    return () => window.removeEventListener(APP_EVENTS.HOME_READY, handleHomeReady);
  }, []);

  useEffect(() => {
    if (location.pathname !== ROUTES.HOME) {
      setHomeReady(false);
      setStartupPromotion(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      !homeReady ||
      location.pathname !== ROUTES.HOME ||
      !auth.access() ||
      !user ||
      promotionHandledForPageSession ||
      startupPromotion
    ) return;

    const promotion = getEligiblePremiumPromotion(user);
    if (!promotion) return;

    const configuredDelay = Number(import.meta.env.VITE_PREMIUM_PROMOTION_DELAY_MS);
    const delay = Number.isFinite(configuredDelay) && configuredDelay >= 0 ? configuredDelay : 2000;
    const timer = window.setTimeout(() => {
      setPromotionHandledForPageSession(true);
      setStartupPromotion({ promotion, user });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [
    homeReady,
    location.pathname,
    promotionHandledForPageSession,
    startupPromotion,
    user?.email,
    user?.id,
    user?.is_guest,
    user?.is_new_user,
    user?.is_premium,
    user?.msisdn,
  ]);

  const handleGoPremium = (entryPoint: string) => {
    trackClickGoPremium(entryPoint);
    const promotion = getEligiblePremiumPromotion(user);
    if (promotion && user) {
      setPromotionHandledForPageSession(true);
      setStartupPromotion({ promotion, user });
      return;
    }
    setIsSubscriptionModalOpen(true);
  };

  const isAuthRoute =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/add-phone" ||
    location.pathname === "/add-password" ||
    location.pathname === "/screen/AddPasswordScreen" ||
    location.pathname.startsWith("/auth/");

  const isMobileFullscreen = 
    isAuthRoute ||
    location.pathname === "/screen/AddPasswordScreen" ||
    location.pathname === "/add-password" ||
    location.pathname === "/screen/PlayScreen" ||
    location.pathname === "/player" ||
    location.pathname === "/play" ||
    location.pathname === "/screen/QueueScreen" ||
    location.pathname === "/queue" ||
    location.pathname.startsWith("/subscription") ||
    location.pathname.startsWith("/screen/PackageScreen") ||
    location.pathname.startsWith("/screen/MyPackageScreen") ||
    location.pathname.startsWith("/screen/CRBTUnsubscribeOTP") ||
    location.pathname.startsWith("/audio-library") || 
    location.pathname.startsWith("/screen/DownloadAudioLibraryScreen") ||
    location.pathname.startsWith("/download") ||
    location.pathname === "/downloadapp" ||
    location.pathname === "/language" ||
    location.pathname === "/screen/LanguageScreen" ||
    location.pathname.startsWith("/privacy") ||
    location.pathname === "/screen/settings/PrivacyPolicy" ||
    location.pathname.startsWith("/screen/settings/") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/security") ||
    location.pathname.startsWith("/auth/ForgotPasswordScreen") ||
    location.pathname === "/forgot-password" ||
    location.pathname.startsWith("/profile") ||
    location.pathname.startsWith("/notifications") ||
    location.pathname.startsWith("/screen/NotificationScreen") ||
    location.pathname.startsWith("/playlists/") ||
    location.pathname.startsWith("/playlist/") ||
    location.pathname.startsWith("/artists/") ||
    location.pathname.startsWith("/artist/") ||
    location.pathname.startsWith("/screen/ArtistDetail") ||
    location.pathname.startsWith("/albums/") ||
    location.pathname.startsWith("/album/") ||
    location.pathname.startsWith("/screen/Album") ||
    location.pathname.startsWith("/playlists/create") ||
    location.pathname.startsWith("/screen/createplaylist") ||
    location.pathname === "/create-playlist" ||
    location.pathname.startsWith("/videos/") ||
    location.pathname.startsWith("/mtv/") ||
    location.pathname.startsWith("/screen/VideoPlayScreen") ||
    location.pathname.startsWith("/screen/VideoCommentsScreen") ||
    location.pathname.startsWith("/screen/VoiceTest") ||
    location.pathname === "/voice-test" ||
    location.pathname.startsWith("/radio") ||
    location.pathname.startsWith("/screen/Radio") ||
    ["/uploaded-songs", "/upload-songs", "/upload-progress", "/search"].includes(location.pathname);

  // Desktop fullscreen: only standalone authentication and full-screen video playback
  const isDesktopFullscreen =
    isAuthRoute ||
    location.pathname === "/screen/VideoPlayScreen";

  // Desktop centered container routes (forms, settings, profile, utilities, tests)
  const isCenteredDesktopRoute =
    location.pathname.startsWith("/subscription") ||
    location.pathname.startsWith("/screen/PackageScreen") ||
    location.pathname.startsWith("/screen/MyPackageScreen") ||
    location.pathname.startsWith("/screen/CRBTUnsubscribeOTP") ||
    (location.pathname.startsWith("/screen/settings/") &&
      !location.pathname.toLowerCase().includes("notification") &&
      !location.pathname.toLowerCase().includes("privacy") &&
      location.pathname !== "/screen/settings/Security" &&
      location.pathname !== "/screen/settings/security" &&
      location.pathname !== "/screen/settings/ChangePassword" &&
      location.pathname !== "/screen/settings/changepassword" &&
      location.pathname !== "/screen/settings/ChangeEmail" &&
      location.pathname !== "/screen/settings/changeemail" &&
      location.pathname !== "/screen/settings/ChangePhone" &&
      location.pathname !== "/screen/settings/changephone" &&
      location.pathname !== "/screen/settings/LoginHistory" &&
      location.pathname !== "/screen/settings/loginhistory" &&
      location.pathname !== "/screen/settings/ArtistUsage" &&
      location.pathname !== "/screen/settings/artistusage") ||
    (location.pathname.startsWith("/settings/") &&
      !location.pathname.toLowerCase().includes("notification") &&
      location.pathname !== "/settings/security" &&
      location.pathname !== "/settings/password" &&
      location.pathname !== "/settings/email" &&
      location.pathname !== "/settings/phone" &&
      location.pathname !== "/settings/login-history" &&
      location.pathname !== "/settings/artist-usage") ||
    (location.pathname.startsWith("/security") &&
      location.pathname !== "/security") ||
    location.pathname === "/add-to-playlist" ||
    location.pathname === "/confirm-playlist" ||
    location.pathname.startsWith("/screen/AddToPlaylist") ||
    location.pathname.startsWith("/screen/ConfirmPlaylist") ||
    [
      "/downloadapp",
      "/download-app",
      "/download"
    ].includes(location.pathname);

  const isMiniPlayerHidden =
    location.pathname === "/screen/PlayScreen" ||
    location.pathname === "/player" ||
    location.pathname === "/play" ||
    location.pathname.startsWith("/videos/") ||
    location.pathname.startsWith("/mtv/") ||
    location.pathname.startsWith("/screen/VideoPlayScreen") ||
    location.pathname.startsWith("/screen/VideoCommentsScreen") ||
    isAuthRoute;

  const isMiniPlayerShowing = !!current && !isMiniPlayerHidden;
  const isAtomSubscriptionRoute = location.pathname === ROUTES.SUBSCRIPTIONS.ATOM_PATH;

  const immersiveDetail = /^\/(playlists|playlist|artists|artist|videos|mtv|albums|album)\//.test(location.pathname) || 
    location.pathname.startsWith("/screen/ArtistDetail") ||
    location.pathname.startsWith("/screen/Album") ||
    location.pathname === "/search";

  useEffect(() => {
    const updateConnection = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);
    return () => {
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
    };
  }, []);

  useEffect(() => {
    const handleExpired = () => {
      if (location.pathname !== ROUTES.AUTH.LOGIN) {
        navigate(ROUTES.AUTH.LOGIN, { state: { from: `${location.pathname}${location.search}` } });
      }
    };
    window.addEventListener("ym:auth-expired", handleExpired);
    return () => window.removeEventListener("ym:auth-expired", handleExpired);
  }, [location, navigate]);

  return (
    <div className={styles.app}>
      {!online && <div className={styles.offlineNotice} role="status">You are offline. Showing content already available on this device.</div>}
      {!isDesktopFullscreen && (
        <aside className={styles.sidebar}>
          <button className={styles.sideBrand} onClick={() => navigate(ROUTES.HOME)} aria-label="Yours Music home">
            <img src="/logo.png" width="44" height="44" alt="" />
            <span><strong>MUSIC</strong><small>Powered by Yours</small></span>
          </button>
          <p className={styles.navLabel}>{t("home.discover")}</p>
          <nav className={styles.sideTabs} aria-label="Primary navigation">
            {tabs.map(({ to, Icon, key }) => (
              <NavLink key={to} to={to} end={to === ROUTES.HOME} aria-label={t(`navigation.${key}`)}>
                <Icon size={23} />
                <b>{t(`navigation.${key}`)}</b>
              </NavLink>
            ))}
          </nav>
          <div className={styles.sideFooter}>
            {!isUserPremium() && (
              <button
                className={styles.sidePremium}
                onClick={() => handleGoPremium("sidebar_btn")}
              >
                <span><SparklesIcon size={22} /></span>
                <strong>Go Premium</strong>
                <small>Listen without limits</small>
              </button>
            )}
            {user && !user.is_guest ? (
              <button className={styles.sideProfile} onClick={() => navigate(ROUTES.MENU)} aria-label="Account menu">
                <img src={avatarUrl} alt="" className={styles.sideAvatarImg} />
                <b>{user.name || user.username}</b>
              </button>
            ) : (
              <button className={styles.sideLogin} onClick={() => navigate(ROUTES.AUTH.LOGIN)}>Log in</button>
            )}
          </div>
        </aside>
      )}

      <div className={`${styles.workspace} ${immersiveDetail ? styles.detailWorkspace : ""} ${isDesktopFullscreen ? styles.fullscreenWorkspace : ""}`}>
        {!isDesktopFullscreen && (
          <header className={`${styles.header} ${immersiveDetail ? styles.detailHeader : ""} ${isMobileFullscreen ? styles.mobileHiddenHeader : ""}`}>
            <div className={styles.utility}>
              <button className={styles.brand} onClick={() => navigate(ROUTES.HOME)} aria-label="Yours Music home">
                <span><strong>MUSIC</strong><small>Powered by Yours</small></span>
                <img src="/logo.png" width="38" height="38" alt="" />
              </button>
              <button className={styles.search} onClick={() => navigate(ROUTES.SEARCH)}>
                <span><SearchIcon size={19} /></span><b>Search music, artists and playlists</b>
              </button>
            <div className={styles.actions}>
              {!isUserPremium() && (
                <>
                  <button
                    className={styles.premium}
                    onClick={() => handleGoPremium("header_btn")}
                  >
                    <SparklesIcon size={17} /> Premium
                  </button>
                  <button
                    className={styles.sparklesMobile}
                    onClick={() => handleGoPremium("mobile_sparkles_btn")}
                    aria-label="Go Premium"
                  >
                    <SparklesIcon size={25} />
                  </button>
                </>
              )}
              <button className={styles.plusButton} onClick={() => setIsAddModalOpen(true)} aria-label="Add or Create" title="Add or Create"><PlusIcon size={30} /></button>
              <button className={styles.iconButton} onClick={() => navigate(ROUTES.SEARCH)} aria-label="Search"><SearchIcon size={25} /></button>
              <button className={styles.bellButton} onClick={() => navigate(ROUTES.NOTIFICATIONS)} aria-label="Notifications" title="Notifications"><BellIcon size={25} /></button>
              {user && !user.is_guest ? (
                <button className={styles.profile} onClick={() => navigate(ROUTES.MENU)} aria-label="Account menu">
                  <img src={avatarUrl} alt="" className={styles.avatarImg} />
                  <b>{user.name || user.username}</b>
                </button>
              ) : (
                <button className={styles.login} onClick={() => navigate(ROUTES.AUTH.LOGIN)}>Log in</button>
              )}
            </div>
          </div>
          <nav className={styles.tabs} aria-label="Primary navigation">
            {tabs.map(({ to, Icon, key }) => (
              <NavLink key={to} to={to} end={to === ROUTES.HOME} aria-label={t(`navigation.${key}`)}>
                <Icon size={25} />
                <b>{t(`navigation.${key}`)}</b>
              </NavLink>
            ))}
          </nav>
        </header>
      )}

      <main className={`${styles.main} ${isCenteredDesktopRoute ? styles.centeredMain : ""} ${isAtomSubscriptionRoute ? styles.atomSubscriptionMain : ""} ${isMiniPlayerShowing ? styles.mainWithPlayer : ""}`}>{children}</main>
      </div>

      <MiniPlayer isFullscreen={isDesktopFullscreen} />
      <GlobalAudio />

      <AddActionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onShowToast={(msg) => {
          setToastMessage(msg);
          setTimeout(() => {
            setToastMessage((prev) => (prev === msg ? null : prev));
          }, 2500);
        }}
      />

      <SubscriptionPromptModal
        visible={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
      />

      {startupPromotion && (
        <StartupPremiumPromotion
          promotion={startupPromotion.promotion}
          user={startupPromotion.user}
          onClose={() => setStartupPromotion(null)}
        />
      )}

      {(user?.msisdn || user?.username) && (
        <WebSocketNotification msisdn={user.msisdn} username={user.username} />
      )}

      {toastMessage && (
        <div className={styles.appToast} role="status">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
